/**
 * RESPONSIBILITY: draw the footer band.
 *
 * Content in, markup out. Columns come from the rows' `column` value rather than from
 * markup, so adding one is content — which is why the grid counts them instead of
 * assuming three.
 */
import { Html } from '@framework/react';
import type { FooterSection, FooterLink } from '@app/sections';
import { SocialIcon } from './SocialIcon';

const columnNames = (links: FooterLink[]): string[] =>
  [...new Set(links.map((l) => l.column?.trim()).filter(Boolean) as string[])];

export function Footer({ c }: { c: FooterSection }) {
  const links = Array.isArray(c.links) ? c.links : [];
  const social = Array.isArray(c.social) ? c.social : [];
  const columns = columnNames(links);

  return (
    <footer className="site-footer">
      <div className="footer-grid" style={{ '--footer-cols': columns.length + 1 } as React.CSSProperties}>
        <div className="footer-about">
          {c.worshipAddressHtml && (
            <div className="footer-addr">
              <span>{c.worshipLabel}</span>
              <Html className="md-inline">{c.worshipAddressHtml}</Html>
            </div>
          )}

          <div className="footer-btns">
            {c.visitLabel && <a className="footer-btn" href={c.visitHref || '#'}>{c.visitLabel}</a>}
            {c.contactLabel && <a className="footer-btn" href={c.contactHref || '#'}>{c.contactLabel}</a>}
          </div>
        </div>

        {columns.map((name) => (
          <div key={name}>
            <h4>{name}</h4>
            <ul>
              {links.filter((l) => l.column?.trim() === name).map((l) => (
                <li key={l.label}><a href={l.href || '#'}>{l.label}</a></li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="footer-base">
        <span>{c.copyright}</span>
        <span className="footer-social">
          {social.map((s) => (
            <a key={s.label} href={s.href || '#'} aria-label={s.label} target="_blank" rel="noopener noreferrer">
              <SocialIcon name={s.icon} fallback={s.label.charAt(0)} />
            </a>
          ))}
        </span>
      </div>
    </footer>
  );
}
